import { SvgIconProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ElementType } from 'react';
import {
  DimensionNoteContainer,
  LabelText,
  StyledStack,
  TextValue,
  TextValueContainer,
  Title,
  TitleContainer,
  Value,
} from './styles';
import { Flex } from '@/styles/globalStyles';
import { useTranslation } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';

interface DimensionNoteProps {
  title: {
    text: string;
    color?: string;
    variant?: 'subtitle2' | 'body2';
  };
  icon?: {
    component: ElementType<SvgIconProps>;
    color?: string;
  };
  label?: {
    opportunity: { icon: ElementType<SvgIconProps>; text: string };
    risk: { icon: ElementType<SvgIconProps>; text: string };
  };
  note?: {
    opportunity: number | string;
    risk: number | string;
    isGeneralScore?: boolean;
    highText?: string;
    lowText?: string;
  };
}

export const DimensionNote = ({ icon: Icon, title, label, note }: DimensionNoteProps) => {
  const theme = useTheme();

  const { t } = useTranslation();

  const { opportunity, risk } = label ?? {};

  const OpportunityIcon = opportunity?.icon;
  const RiskIcon = risk?.icon;

  const textValueVariant = note?.isGeneralScore ? 'subtitle1' : 'subtitle2';

  const textValueColor = (variant: 'opportunity' | 'risk') => {
    const value = variant === 'opportunity' ? note?.opportunity : note?.risk;

    // Return lighter gray when value is 0 (N/A)
    if (value == 0) {
      return theme.palette.text.disabled;
    }

    if (
      (risk?.text || title.text === t('page.municipality.municipality.card.scores.footer')) &&
      variant === 'risk'
    ) {
      return theme.palette.orange[800];
    }
    if (
      (opportunity?.text ||
        title.text === t('page.municipality.municipality.card.scores.footer')) &&
      variant === 'opportunity'
    ) {
      return theme.palette.primary.main;
    }
    return 'text.primary';
  };

  const textValueFontWeight = (variant: 'opportunity' | 'risk') => {
    const value = variant === 'opportunity' ? note?.opportunity : note?.risk;
    return value;
  };

  return (
    <DimensionNoteContainer>
      <StyledStack direction="row" spacing={1.5}>
        {Icon?.component && (
          <Icon.component sx={{ color: Icon.color ?? theme.palette.action.active }} />
        )}

        <TitleContainer>
          <Title variant={title.variant ?? 'body2'} color={title.color}>
            <SafeMarkdown text={title.text || ''} />
          </Title>
        </TitleContainer>
      </StyledStack>

      <TextValueContainer>
        <TextValue>
          {OpportunityIcon && <OpportunityIcon sx={{ color: textValueColor('opportunity') }} />}

          <Flex direction="column" align="center">
            <Value
              variant={textValueVariant}
              color={textValueColor('opportunity')}
              fontWeight={textValueFontWeight('opportunity')}
            >
              {label?.opportunity?.text ?? (note?.opportunity == 0 ? 'N/A' : note?.opportunity)}
            </Value>

            {note?.highText && (
              <LabelText variant="body2" color="text.secondary">
                <SafeMarkdown text={note?.highText || ''} />
              </LabelText>
            )}
          </Flex>
        </TextValue>

        <TextValue>
          {RiskIcon && <RiskIcon sx={{ color: textValueColor('risk') }} />}
          <Flex direction="column" align="center">
            <Value
              variant={textValueVariant}
              color={textValueColor('risk')}
              fontWeight={textValueFontWeight('risk')}
            >
              {label?.risk?.text ?? (note?.risk == 0 ? 'N/A' : note?.risk)}
            </Value>

            {note?.lowText && (
              <LabelText variant="body2" color="text.secondary">
                <SafeMarkdown text={note?.lowText || ''} />
              </LabelText>
            )}
          </Flex>
        </TextValue>
      </TextValueContainer>
    </DimensionNoteContainer>
  );
};
